import { useFlags } from "launchdarkly-react-client-sdk";

import { Navigate } from "src/router";
import type { RouteObject } from "src/router";
import type { RoutePreload } from "src/router/route-preloads";
import { PermissionedRoute } from "src/components/router/permissioned-route";
import { lazyNamed, lazyWithRetry } from "src/utils/lazy-named";
import {
  syncsLoader,
  syncDetailLoader,
  syncConfigurationLoader,
} from "./loader";

const syncsImport = () => import("./syncs");
const Syncs = lazyWithRetry(syncsImport);

export const preloads: RoutePreload[] = [["/syncs", syncsImport]];
const SyncWrapper = lazyNamed(() => import("./sync"), "SyncWrapper");
const SyncOverview = lazyNamed(() => import("./sync/overview"), "Overview");
const SyncConfiguration = lazyNamed(
  () => import("./sync/configuration"),
  "Configuration",
);
const SyncSchedule = lazyNamed(() => import("./sync/schedule"), "Schedule");
const SyncRuns = lazyNamed(() => import("./sync/runs"), "Runs");
const SyncAlerts = lazyNamed(() => import("./sync/alerts"), "Alerts");
const Alerting = lazyNamed(() => import("./sync/alerting"), "Alerting");
const SyncLogs = lazyNamed(() => import("./sync/logs"), "Logs");
const SyncSplits = lazyNamed(() => import("./sync/splits"), "Splits");
const SyncActivity = lazyNamed(() => import("./sync/activity"), "Activity");
const SyncDraft = lazyNamed(() => import("./sync/draft"), "SyncDraft");
const SyncRunWrapper = lazyNamed(() => import("./sync/run"), "SyncRunWrapper");
const SyncRunRejectedRows = lazyNamed(
  () => import("./sync/run/rows"),
  "RejectedRows",
);
const SyncRunSuccessfulRows = lazyNamed(
  () => import("./sync/run/rows"),
  "SuccessfulRows",
);
const SyncRunConfiguration = lazyNamed(
  () => import("./sync/run/configuration"),
  "ConfigurationPage",
);
const SyncRunDebug = lazyNamed(() => import("./sync/run/debug"), "RunDebug");
const SyncRunSummary = lazyNamed(
  () => import("./sync/run/summary"),
  "SyncRunSummary",
);
const CreateSyncWizard = lazyNamed(
  () => import("./create/wizards"),
  "CreateSyncWizard",
);
const RealtimeSyncWrapper = lazyNamed(
  () => import("./realtime-syncs"),
  "RealtimeSyncWrapper",
);
const RealtimeSyncOverview = lazyNamed(
  () => import("./realtime-syncs/overview"),
  "RealtimeSyncOverview",
);
const RealtimeSyncConfiguration = lazyNamed(
  () => import("./realtime-syncs/configuration"),
  "RealtimeSyncConfiguration",
);
const RealtimeSyncActivity = lazyNamed(
  () => import("./realtime-syncs/activity"),
  "RealtimeSyncActivity",
);
const InternalDebugger = lazyNamed(
  () => import("src/pages/syncs/sync/run/internal-debugger"),
  "InternalDebugger",
);
const RealtimeSyncFailuresRetrying = lazyNamed(
  () => import("./realtime-syncs/errors"),
  "RealtimeSyncFailuresRetrying",
);
const RealtimeSyncFailuresArchived = lazyNamed(
  () => import("./realtime-syncs/errors"),
  "RealtimeSyncFailuresArchived",
);

// Temporary until the run summary page is enabled for all users
const SyncRunRedirect = () => {
  const { appSyncRunSummaryPageEnabled } = useFlags();

  const pathname = appSyncRunSummaryPageEnabled ? "summary" : "successful";
  return <Navigate to={{ pathname, search: location?.search }} replace />;
};

const SyncDebugPermissionedRoute = ({ children }) => {
  const { appShowDebugSuggestionOnly } = useFlags();
  if (appShowDebugSuggestionOnly) return children;
  return (
    <PermissionedRoute
      permission={{
        v2: { resource: "sync", grant: "can_debug", id: "sync_id" },
      }}
      redirect="/syncs/:sync_id"
    >
      {children}
    </PermissionedRoute>
  );
};

// All sync routes are accessible from space-restricted views
export const routes: Array<RouteObject> = [
  {
    path: "syncs",
    spacesAccess: "accessible-from-limited-view",
    loader: syncsLoader,
    element: <Syncs />,
  },
  {
    path: "syncs/:sync_id/*",
    spacesAccess: "accessible-from-limited-view",
    loader: syncDetailLoader,
    element: <SyncWrapper />,
    children: [
      {
        index: true,
        spacesAccess: "accessible-from-limited-view",
        element: (
          <Navigate
            to={{ pathname: "overview", search: location?.search }}
            replace
          />
        ),
      },
      {
        path: "overview",
        spacesAccess: "accessible-from-limited-view",
        element: <SyncOverview />,
      },
      {
        path: "configuration",
        spacesAccess: "accessible-from-limited-view",
        loader: syncConfigurationLoader,
        element: <SyncConfiguration />,
      },
      {
        path: "schedule",
        spacesAccess: "accessible-from-limited-view",
        element: <SyncSchedule />,
      },
      {
        path: "runs",
        spacesAccess: "accessible-from-limited-view",
        element: <SyncRuns />,
      },
      {
        path: "alerts",
        spacesAccess: "accessible-from-limited-view",
        element: <SyncAlerts />,
      },
      {
        path: "alerting",
        spacesAccess: "accessible-from-limited-view",
        element: <Alerting />,
      },
      {
        path: "logs",
        spacesAccess: "accessible-from-limited-view",
        element: <SyncLogs />,
      },
      {
        path: "splits",
        spacesAccess: "accessible-from-limited-view",
        element: <SyncSplits />,
      },
      {
        path: "activity",
        spacesAccess: "accessible-from-limited-view",
        element: <SyncActivity />,
      },
    ],
  },
  {
    path: "realtime-syncs/:sync_id/*",
    spacesAccess: "accessible-from-limited-view",
    element: <RealtimeSyncWrapper />,
    children: [
      {
        index: true,
        spacesAccess: "accessible-from-limited-view",
        element: (
          <Navigate
            to={{ pathname: "overview", search: location?.search }}
            replace
          />
        ),
      },
      {
        path: "overview",
        spacesAccess: "accessible-from-limited-view",
        element: <RealtimeSyncOverview />,
      },
      {
        path: "configuration",
        spacesAccess: "accessible-from-limited-view",
        element: <RealtimeSyncConfiguration />,
      },
      {
        // Multi-event destinations (e.g. Iterable, Braze) navigate here when
        // selecting a standard/custom event from the configuration editor.
        path: "configuration/event/:event?",
        spacesAccess: "accessible-from-limited-view",
        element: <RealtimeSyncConfiguration />,
      },
      {
        path: "errors",
        spacesAccess: "hidden-from-limited-view",
        children: [
          {
            index: true,
            spacesAccess: "hidden-from-limited-view",
            element: <Navigate to="retrying" replace />,
          },
          {
            path: "archived",
            spacesAccess: "hidden-from-limited-view",
            element: <RealtimeSyncFailuresArchived />,
          },
          {
            path: "retrying",
            spacesAccess: "hidden-from-limited-view",
            element: <RealtimeSyncFailuresRetrying />,
          },
        ],
      },
      {
        path: "activity",
        spacesAccess: "accessible-from-limited-view",
        element: <RealtimeSyncActivity />,
      },
    ],
  },
  {
    path: "syncs/:sync_id/draft",
    spacesAccess: "accessible-from-limited-view",
    element: <SyncDraft />,
  },
  {
    path: "syncs/:sync_id/runs/:run_id/*",
    spacesAccess: "accessible-from-limited-view",
    element: <SyncRunWrapper />,
    children: [
      {
        index: true,
        spacesAccess: "accessible-from-limited-view",
        element: <SyncRunRedirect />,
      },
      {
        path: "summary",
        spacesAccess: "accessible-from-limited-view",
        element: <SyncRunSummary />,
      },
      {
        path: "successful",
        spacesAccess: "accessible-from-limited-view",
        element: (
          <SyncDebugPermissionedRoute>
            <SyncRunSuccessfulRows />
          </SyncDebugPermissionedRoute>
        ),
        children: [
          {
            path: "debug/:row_id",
            spacesAccess: "accessible-from-limited-view",
            element: <SyncRunDebug />,
          },
        ],
      },
      {
        path: "rejected",
        spacesAccess: "accessible-from-limited-view",
        element: (
          <SyncDebugPermissionedRoute>
            <SyncRunRejectedRows />
          </SyncDebugPermissionedRoute>
        ),
        children: [
          {
            path: "debug/:row_id",
            spacesAccess: "accessible-from-limited-view",
            element: <SyncRunDebug />,
          },
        ],
      },
      {
        path: "configuration",
        spacesAccess: "accessible-from-limited-view",
        element: <SyncRunConfiguration />,
      },
      {
        path: "internal-debugger",
        spacesAccess: "accessible-from-limited-view",
        element: <InternalDebugger />,
      },
    ],
  },
  {
    path: "syncs/new",
    spacesAccess: "accessible-from-limited-view",
    element: <CreateSyncWizard />,
  },
];
